import {
  MarqueeSelection,
  PrimaryButton,
  SearchBox,
  Spinner,
  TooltipHost,
} from "@fluentui/react";
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Selection,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";
import { useEffect, useState } from "react";
import IconButtonItem from "../IconButtonItem/IconButtonItem";

import "./DataTable.scss";

export interface IDataTableProps {
  rowData: any[];
  columns: any[];
  useDynamicColumnCreation?: boolean;
  actionColumnName?: string;
  isLoading?: boolean;
  disablePagination?: boolean;
  actionColumn?: (item: any) => JSX.Element;
  extraInnerProps?: IGenericListProps;
  extraInHeaderItems?: (JSX.Element | null)[];
}

export interface IPaginatorProps {
  max: number;
  emitPage: (page: number, perPage: number) => void;
}

export const PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100];

const DEFAULT_INITIAL_PAGES = 10;

const dateFormat_full_jsx = (isoDate: string) => {
  return (
    <div>
      <div>{isoDate.split("T")[0]}</div>
      <small>{new Date(isoDate).toLocaleTimeString()}</small>
    </div>
  );
};

const dateFormat_full_s = (isoDate: string) => {
  return isoDate.split("T")[0] + " " + new Date(isoDate).toLocaleTimeString();
};

const dateFormat_s = (isoDate: string) => {
  return isoDate.split("T")[0];
};

export const Paginator = (props: IPaginatorProps) => {
  const [page, setPage] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(DEFAULT_INITIAL_PAGES);

  useEffect(() => {
    props.emitPage(page, perPage);
  }, [page, perPage]);

  const maxPage = Math.ceil(props.max / perPage);
  const currPage = props.max === 0 ? 0 : page + 1;
  const displayBottom = perPage * page + 1;
  const displayTop = currPage * perPage;
  const maxDisplay = displayTop > props.max ? props.max : displayTop;
  const minDisplay = displayBottom > props.max ? props.max : displayBottom;
  return (
    <div>
      <div className="pagination-main-wrap">
        <IconButtonItem
          disabled={page === 0}
          onClick={() => {
            setPage(page - 1);
          }}
          tooltip="Pagina precedente"
          iconName="ChevronLeft"
        />
        <div className="table-paginator-text">
          Pagina {currPage} di {maxPage}, righe da {minDisplay} a {maxDisplay}{" "}
          su {props.max}, per pagina:
          <select
            defaultValue={DEFAULT_INITIAL_PAGES}
            className="paginator-select"
            onChange={(e: any) => {
              setPage(0);
              setPerPage(e.target.value);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((x: number, i: number) => {
              return (
                <option key={i} value={x}>
                  {x}
                </option>
              );
            })}
          </select>
        </div>
        <IconButtonItem
          disabled={currPage >= maxPage}
          onClick={() => {
            setPage(page + 1);
          }}
          tooltip="Pagina successiva"
          iconName="ChevronRight"
        />
      </div>
    </div>
  );
};

const extractItemValueOnPath = (item: any, path: string) => {
  try {
    let dotPath: string[] = path.split(".");
    for (let i = 0; i < dotPath.length; i++) {
      item = item[dotPath[i]];
    }
    return item;
  } catch (e) {
    return null;
  }
};

export interface IGenericListProps {
  onSelectionChanged?: (items: any[]) => void;
  onItemInvoked?: (item: any) => void;
  allowFilter?: boolean;
  searchText?: string;
  emitPagination?: (pagination: { page: number; perPage: number }) => void;
  emitSearchedText?: (text: string) => void;
  columns?: any[];
  items?: any[];
  selectionMode?: SelectionMode;
  initialSelection?: (item: any) => boolean;
  iconFromField?: string;
  extraHeaderItems?: JSX.Element[];
  rowDynamiClass?: {
    className: string;
    conditionKey: string;
    value: any;
  };
}

export const fromIdPropertyTypeToType = (propType: number) => {
  switch (propType) {
    case 1:
      return "string";
    case 2:
      return "numeric";
    case 3:
      return "date";
    case 4:
      return "date/time";
    case 5:
      return "dropDrown";
    case 6:
      return "boolean";
    default:
      return "string";
  }
};

const GenericList = (props: IGenericListProps) => {
  const [filterValue, setFilterValue] = useState<string>("");
  const [activeFilterValue, setActiveFilterValue] = useState<string>("");
  const [holdOn, setHoldOn] = useState<boolean>(false);
  const [items, setItems] = useState<any[]>([]);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    perPage: number;
    paginating: boolean;
  }>({ page: 0, perPage: DEFAULT_INITIAL_PAGES, paginating: false });
  const [selection] = useState<Selection>(
    new Selection({
      onSelectionChanged: () => {
        if (props.onSelectionChanged) {
          props.onSelectionChanged(selection.getSelection());
        }
      },
    })
  );

  /** callback when row is clicked */
  const onItemInvoked = (item: any): void => {
    if (props.onItemInvoked) {
      props.onItemInvoked(item);
    }
  };

  const generateColumn = (
    index: number,
    name: string,
    fieldName: string,
    dataType: string,
    size: string,
    onRender: any
  ): IColumn => {
    let baseColumn: IColumn = {
      key: "column" + index.toString(),
      name: name,
      fieldName: fieldName,
      minWidth: 0,
      maxWidth: 0,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      data: dataType,
      isPadded: true,
      onRender: (item: any) => {
        let it = extractItemValueOnPath(item, fieldName);
        if (dataType && dataType === "date/time") {
          let dt = dateFormat_full_jsx(it) ?? "";
          return (
            <TooltipHost content={dt} id={"table-row-item_" + index.toString()}>
              <span>{dt}</span>
            </TooltipHost>
          );
        } else if (dataType && dataType === "date") {
          let dt = dateFormat_s(it) ?? "";
          return (
            <TooltipHost content={dt} id={"table-row-item_" + index.toString()}>
              <span>{dt}</span>
            </TooltipHost>
          );
        } else {
          return (
            <TooltipHost content={it} id={"table-row-item_" + index.toString()}>
              <span>{it}</span>
            </TooltipHost>
          );
        }
      },
    };

    /* custom on render function */
    if (onRender) {
      baseColumn["onRender"] = onRender;
    }

    if (size === "tiny") {
      baseColumn["minWidth"] = 16;
      baseColumn["maxWidth"] = 16;
    }

    if (size === "small") {
      baseColumn["minWidth"] = 50;
      baseColumn["maxWidth"] = 80;
    }

    if (size === "medium") {
      baseColumn["minWidth"] = 120;
      baseColumn["maxWidth"] = 140;
    }

    if (size === "bigger") {
      baseColumn["minWidth"] = 160;
      baseColumn["maxWidth"] = 190;
    }

    if (size === "large") {
      baseColumn["minWidth"] = 210;
      baseColumn["maxWidth"] = 350;
    }

    if (size === "larger") {
      baseColumn["minWidth"] = 300;
      baseColumn["maxWidth"] = 500;
    }

    // interruttore con scritta (es. Attivo / Spento)
    if (size === "toggle") {
      baseColumn["minWidth"] = 100;
      baseColumn["maxWidth"] = 110;
    }

    // colonna di pulsanti con etichetta (es. Azioni di Setup > Dispositivo)
    if (size === "actions") {
      baseColumn["minWidth"] = 340;
      baseColumn["maxWidth"] = 420;
    }

    if (size === "wide") {
      baseColumn["minWidth"] = 450;
      baseColumn["maxWidth"] = 600;
    }

    return baseColumn;
  };

  const initData = () => {
    let columns = props.columns ? props.columns : [];
    let items = props.items ? props.items : [];

    let processedColumns: Array<IColumn> = [];
    let editedColumns = [];
    for (let i = 0; i < columns.length; i++) {
      editedColumns.push(columns[i]);
    }

    // generate columns complex objects
    for (let i = 0; i < editedColumns.length; i++) {
      let column = editedColumns[i];
      let complexColumn = generateColumn(
        i,
        column["name"],
        column["fieldName"],
        column["dataType"],
        column["size"],
        column["onRender"]
      );

      processedColumns.push(complexColumn);
    }

    setItems(items);
    setColumns(processedColumns);
  };

  const setInitialSelection = () => {
    if (props.items) {
      if (props.initialSelection && props.items.length > 0) {
        for (let i = 0; i < props.items.length; i++) {
          if (props.initialSelection(props.items[i])) {
            selection.setIndexSelected(i, true, true);
          }
        }
      }
    }
  };

  useEffect(() => {
    initData();
    setTimeout(() => {
      try {
        setInitialSelection();
      } catch (e) {}
    }, 100);
  }, [props.items, props.columns]);

  const filterItems = (rows: any, text: string) => {
    if (text === "" || props.emitSearchedText) {
      return rows;
    }
    let acceptItems: any[] = [];

    for (let i = 0; i < rows.length; i++) {
      for (let j = 0; j < columns.length; j++) {
        let field = columns[j].fieldName;
        if (field) {
          let data = rows[i][field];
          let value = data;
          if (data && data.value !== undefined) {
            value = data.value;
          }
          if (columns[j].data === "date" && value) {
            value = dateFormat_s(value);
          }
          if (columns[j].data === "date/time" && value) {
            value = dateFormat_full_s(value);
          }
          if (
            value &&
            value
              .toString()
              .toLocaleLowerCase()
              .indexOf(text.toLocaleLowerCase()) !== -1
          ) {
            acceptItems.push(rows[i]);
            break;
          }
        }
      }
    }
    return acceptItems;
  };

  const doSearch = () => {
    setHoldOn(true);
    setActiveFilterValue(filterValue);

    if (props.emitSearchedText) {
      props.emitSearchedText(filterValue);
    }

    setTimeout(() => {
      try {
        setHoldOn(false);
      } catch (e) {}
    }, 200);
  };

  useEffect(() => {
    if (props.emitPagination) {
      props.emitPagination({
        page: pagination.page,
        perPage: pagination.perPage,
      });
    }
  }, [pagination]);

  const filteredItems = props.allowFilter
    ? filterItems(items, activeFilterValue)
    : items;

  let rowsFinal = !props.emitPagination
    ? filteredItems.slice(
        pagination.page * pagination.perPage,
        (pagination.page + 1) * pagination.perPage
      )
    : filteredItems;

  setTimeout(() => {
    if (props.rowDynamiClass) {
      let visibleRows = Array.from(
        document.getElementsByClassName("ms-List-cell")
      );
      for (let i = 0; i < rowsFinal.length && i < visibleRows.length; i++) {
        console.log(rowsFinal);
        if (
          rowsFinal[i][props.rowDynamiClass.conditionKey] ===
          props.rowDynamiClass.value
        ) {
          let row: any = visibleRows[i].firstChild?.firstChild;
          row.className += " " + props.rowDynamiClass.className;
        }
      }
    }
  }, 200);

  return (
    <div>
      <div className="generic-list-seach-wrap flex-column-on-small-screen">
        <div>
          {props.extraHeaderItems && (
            <div className="generic-list-seach-wrap-row flex-column-on-small-screen">
              {props.extraHeaderItems.map((x: JSX.Element, i: number) => {
                return <span key={i}>{x}</span>;
              })}
            </div>
          )}
        </div>
        {!holdOn && (
          <div className="generic-list-seach-wrap-row flex-column-on-small-screen">
            <Paginator
              max={filteredItems.length}
              emitPage={(page: number, perPage: number) => {
                setPagination({ page, perPage, paginating: true });
                setTimeout(() => {
                  setPagination({ page, perPage, paginating: false });
                }, 200);
              }}
            />
          </div>
        )}

        {props.allowFilter && (
          <div className="generic-list-seach-wrap-row flex-column-on-small-screen">
            <SearchBox
              placeholder={props.searchText ?? "Cerca..."}
              value={filterValue}
              onChange={(e: any, t: string | undefined) => {
                if (t !== undefined) {
                  setFilterValue(t);
                }
              }}
              onKeyUp={(e: any) => {
                if (e.keyCode === 13) {
                  doSearch();
                }
              }}
              onClear={() => {
                setFilterValue("");
                setActiveFilterValue("");
                if (props.emitSearchedText) {
                  props.emitSearchedText("");
                }
              }}
            />
            <div style={{ width: "0.5em" }}></div>
            <PrimaryButton
              disabled={filterValue === ""}
              text={"Cerca"}
              onClick={() => {
                doSearch();
              }}
            />
          </div>
        )}
      </div>

      <div className="table-zone-wrap">
        <MarqueeSelection selection={selection}>
          <DetailsList
            items={holdOn || pagination.paginating ? [] : rowsFinal}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionPreservedOnEmptyClick={true}
            selectionMode={
              props.selectionMode ? props.selectionMode : SelectionMode.none
            }
            ariaLabelForSelectionColumn="Seleziona"
            ariaLabelForSelectAllCheckbox="Seleziona o deseleziona tutto"
            checkButtonAriaLabel="select row"
            onItemInvoked={onItemInvoked}
            selection={selection}
          />
        </MarqueeSelection>
        {rowsFinal.length === 0 && (
          <div className="no-data-on-list">Nessun dato da mostrare.</div>
        )}
      </div>
    </div>
  );
};

const DataTable = (props: IDataTableProps) => {
  const [rowData, setRowData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);

  useEffect(() => {
    setRowData(props.rowData);
  }, [props.rowData]);

  useEffect(() => {
    setColumns(props.columns);
  }, [props.columns]);

  const getRows = () => {
    if (props.useDynamicColumnCreation) {
      let rows_ = rowData
        .filter((x: any) => x.rowNumber !== -1 && x.gridName)
        .map((x: any) => {
          return x.rowNumber;
        });
      let rows: any[] = Array.from(new Set(rows_));
      let formattedData: any = [];
      for (let i = 0; i < rows.length; i++) {
        let baseObj: any = {};
        let cellsForRow = rowData.filter(
          (x: any) => x.rowNumber === rows[i] && x.gridName
        );
        for (let j = 0; j < cellsForRow.length; j++) {
          baseObj[j] = cellsForRow[j];
        }
        formattedData.push(baseObj);
      }
      return formattedData;
    } else {
      return rowData;
    }
  };

  let extraProps = props.extraInnerProps ? props.extraInnerProps : {};

  return (
    <div className="table-main-wrap">
      {props.isLoading && (
        <div className="spinner-wrap">
          <Spinner label="Loading..." />
        </div>
      )}
      {!props.isLoading && (
        <GenericList columns={columns} items={getRows()} {...extraProps} />
      )}
    </div>
  );
};

export default DataTable;
